import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ChevronDown, Mail } from "lucide-react";
import { apiDelete, apiGet, apiPost } from "../../utils/api";
import { getPlanDisplayName } from "../../utils/pricingDisplay.js";
import { toast } from "../../utils/toast";
import "./admin-shell.css";

/* ───────────────────────── helpers ───────────────────────── */
const PAYMENT_SUCCESS  = new Set(["succeeded", "paid", "captured"]);
const ACTIVE_SUB       = new Set(["active", "trial"]);
const EXPIRY_WARN_DAYS = 14;

const norm  = v => String(v || "").trim();
const normL = v => norm(v).toLowerCase();

function parseTs(v) {
  if (!v) return 0;
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? t : 0;
}

function fmtDate(v, { time = false } = {}) {
  const t = parseTs(v);
  if (!t) return "-";
  try {
    return new Intl.DateTimeFormat("en-IN", {
      dateStyle: "medium",
      ...(time ? { timeStyle: "short" } : {}),
    }).format(new Date(t));
  } catch { return new Date(t).toLocaleString(); }
}

function fmtCurrency(amount, currency = "INR") {
  const n = Number(amount || 0);
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: String(currency || "INR").toUpperCase(),
      maximumFractionDigits: 2,
    }).format(n / 100);
  } catch { return `${(n / 100).toFixed(2)} ${currency}`; }
}

function fmtSeats(v) {
  if (v === null) return "Unlimited";
  const n = Number(v);
  return (!Number.isFinite(n) || n <= 0) ? "5" : `${n}`;
}

function getPlanLabel(sub) {
  if (!sub) return "No Plan";
  return getPlanDisplayName(sub.planId, sub.planName) || "No Plan";
}

function cmpRecency(a, b, fields = ["updatedAt", "createdAt"]) {
  for (const f of fields) {
    const d = parseTs(b?.[f]) - parseTs(a?.[f]);
    if (d !== 0) return d;
  }
  return 0;
}

function resolveCurrentSub(rows = []) {
  if (!rows.length) return null;
  return [...rows].sort((a, b) => {
    const ap = ACTIVE_SUB.has(normL(a?.status)) ? 2 : normL(a?.status) === "past_due" ? 1 : 0;
    const bp = ACTIVE_SUB.has(normL(b?.status)) ? 2 : normL(b?.status) === "past_due" ? 1 : 0;
    if (ap !== bp) return bp - ap;
    const ed = parseTs(b?.expiresAt) - parseTs(a?.expiresAt);
    if (ed !== 0) return ed;
    return cmpRecency(a, b, ["updatedAt", "createdAt", "startsAt"]);
  })[0] || null;
}

function resolveLifecycle(sub) {
  if (!sub) return "no_plan";
  const st = normL(sub.status);
  const exp = parseTs(sub.expiresAt);
  if (exp && exp < Date.now()) return "expired";
  return st || "inactive";
}

function resolveExpiry(sub) {
  const exp = parseTs(sub?.expiresAt);
  if (!exp) return { label: "No expiry", tone: "neutral", days: null };
  const days = Math.ceil((exp - Date.now()) / 86400000);
  if (days < 0)                 return { label: `Expired ${Math.abs(days)}d ago`, tone: "danger",  days };
  if (days <= EXPIRY_WARN_DAYS) return { label: `Expires in ${days}d`,            tone: "warning", days };
  return                                { label: `${days}d remaining`,             tone: "healthy", days };
}

function summarizeIssues({ lifecycle, failedPays = [], expiry }) {
  const out = [];
  if (failedPays.length > 0) out.push(`${failedPays.length} failed payment${failedPays.length > 1 ? "s" : ""}`);
  if (lifecycle === "expired")    out.push("plan expired");
  if (lifecycle === "past_due")   out.push("payment overdue");
  if (expiry?.tone === "warning") out.push("expiry soon");
  return out;
}

function latestOf(rows = [], fields) {
  if (!rows.length) return null;
  return [...rows].sort((a, b) => cmpRecency(a, b, fields))[0];
}
function latestPaid(rows = []) {
  return latestOf(rows.filter(r => PAYMENT_SUCCESS.has(normL(r?.status))), ["paidAt", "updatedAt", "createdAt"]);
}

function buildRowFromParts(t, subs, pays, invs, fails, emails) {
  const tid = norm(t?.id);
  const sortedPays  = [...pays].sort((a, b) => cmpRecency(a, b, ["paidAt", "createdAt"]));
  const sortedInvs  = [...invs].sort((a, b) => cmpRecency(a, b, ["paidAt", "createdAt"]));
  const sortedFails = [...fails].sort((a, b) => cmpRecency(a, b, ["paidAt", "createdAt"]));
  const sortedEmails= [...emails].sort((a, b) => cmpRecency(a, b, ["sentAt", "createdAt"]));

  const curSub    = resolveCurrentSub(subs);
  const latSub    = latestOf(subs, ["updatedAt", "createdAt", "startsAt"]);
  const effSub    = curSub || latSub;
  const lifecycle = resolveLifecycle(effSub);
  const expiry    = resolveExpiry(effSub);
  const issues    = summarizeIssues({ lifecycle, failedPays: sortedFails, expiry });

  return {
    tenantId: tid,
    tenantName: norm(t?.name) || "Unnamed Tenant",
    ownerEmail: normL(t?.ownerEmail) || "-",
    billingEmail: normL(effSub?.billingEmail) || normL(t?.ownerEmail) || "-",
    enrollmentKey: norm(t?.enrollmentKey),
    deviceCount: Number(t?.deviceCount || 0),
    licensedSeats: t?.maxSeats === null ? null : (Number.isFinite(Number(t?.maxSeats)) ? Number(t.maxSeats) : 5),
    isTenantActive: !!t?.isActive,
    curSub, effSub, lifecycle, expiry, issues,
    latestPay:    latestOf(sortedPays, ["paidAt", "createdAt"]),
    lastPaidPay:  latestPaid(sortedPays),
    latestInv:    latestOf(sortedInvs, ["paidAt", "createdAt"]),
    failedPayCount: sortedFails.length,
    subHistory: subs, payHistory: sortedPays, invHistory: sortedInvs,
    failHistory: sortedFails, emailHistory: sortedEmails,
  };
}

function statusClass(raw) {
  const s = normL(raw);
  if (["active", "trial", "succeeded", "paid", "captured"].includes(s)) return "is-active";
  if (["past_due", "pending", "no_payment", "processing"].includes(s))  return "is-pending";
  if (["expired", "canceled", "cancelled", "no_plan", "failed", "inactive"].includes(s)) return "is-disabled";
  return "is-pending";
}

function StatusBadge({ value }) {
  const raw = normL(value) || "unknown";
  return <span className={`admin-status ${statusClass(raw)}`}>{raw.replace(/_/g, " ")}</span>;
}

function ExpiryBadge({ meta }) {
  if (!meta) return null;
  const cls = meta.tone === "healthy" ? "is-active" : meta.tone === "warning" ? "is-pending" : meta.tone === "danger" ? "is-disabled" : "";
  return <span className={`admin-status ${cls}`}>{meta.label}</span>;
}

function MoneyLine({ src }) {
  const sub   = Number(src?.subtotalAmount ?? src?.subtotal ?? src?.amount ?? 0);
  const tax   = Number(src?.taxAmount || 0);
  const total = Number(src?.totalAmount ?? src?.total ?? (sub + tax));
  const cur   = String(src?.currency || "INR").toUpperCase();
  return (
    <div style={{ fontSize: "0.78rem", color: "var(--as-text-2)", lineHeight: 1.6 }}>
      <div>Base: {fmtCurrency(sub, cur)}</div>
      <div>GST:  {fmtCurrency(tax, cur)}</div>
      <div style={{ fontWeight: 600, color: "var(--as-text)" }}>Total: {fmtCurrency(total, cur)}</div>
    </div>
  );
}

/* ───────────────────────── tabs ───────────────────────── */
const DETAIL_TABS = [
  { id: "snapshot",      label: "Snapshot"       },
  { id: "subscriptions", label: "Subscriptions"  },
  { id: "payments",      label: "Payments"       },
  { id: "invoices",      label: "Invoices"       },
  { id: "failed",        label: "Failed Payments"},
  { id: "emails",        label: "Email Log"      },
];

/* ───────────────────────── component ───────────────────────── */
export default function AdminBillingTenantDetail() {
  const { tenantId }  = useParams();
  const location      = useLocation();
  const navigate      = useNavigate();

  const stateRow = location.state?.row || null;

  const [loading,     setLoading]     = useState(!stateRow);
  const [row,         setRow]         = useState(stateRow);
  const [detailTab,   setDetailTab]   = useState("snapshot");
  const [resendingId, setResendingId] = useState("");
  const [resettingId, setResettingId] = useState("");

  /* ── Load fresh data if no state ── */
  useEffect(() => {
    if (stateRow) return;
    void loadData();
  }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    setLoading(true);
    try {
      const enc = encodeURIComponent(tenantId);
      const [tR, sR, pR, iR, fR, eR] = await Promise.all([
        apiGet(`/api/admin/tenants/${enc}`),
        apiGet(`/api/admin/billing/tenants/${enc}/subscriptions`),
        apiGet(`/api/admin/billing/tenants/${enc}/payments`),
        apiGet(`/api/admin/billing/tenants/${enc}/invoices`),
        apiGet(`/api/admin/billing/tenants/${enc}/failed-payments`).catch(() => null),
        apiGet(`/api/admin/billing/tenants/${enc}/email-logs`).catch(() => null),
      ]);
      const [tp, sp, pp, ip, fp, ep] = await Promise.all([
        tR.json().catch(() => ({})),
        sR.json().catch(() => null),
        pR.json().catch(() => null),
        iR.json().catch(() => null),
        fR ? fR.json().catch(() => null) : Promise.resolve(null),
        eR ? eR.json().catch(() => null) : Promise.resolve(null),
      ]);
      if (!tR.ok) throw new Error(tp?.message || "Failed to load tenant");
      const tenant = Array.isArray(tp) ? (tp.find(t => norm(t?.id) === tenantId) || tp[0] || {}) : tp;
      const built = buildRowFromParts(
        { ...tenant, id: tenantId },
        Array.isArray(sp?.subscriptions) ? sp.subscriptions : [],
        Array.isArray(pp?.payments)      ? pp.payments       : [],
        Array.isArray(ip?.invoices)      ? ip.invoices       : [],
        Array.isArray(fp?.payments)      ? fp.payments       : [],
        Array.isArray(ep?.logs)          ? ep.logs           : [],
      );
      setRow(built);
    } catch (err) {
      toast.error(err?.message || "Failed to load tenant billing");
    } finally {
      setLoading(false);
    }
  };

  /* ── Actions ── */
  const resendInvoice = async (inv) => {
    const id = norm(inv?.id);
    if (!id) return;
    if (!window.confirm(`Resend invoice ${inv.invoiceNumber || id}?`)) return;
    setResendingId(id);
    try {
      const res = await apiPost(`/api/admin/billing/invoices/${encodeURIComponent(id)}/resend`);
      const d = await res.json().catch(() => null);
      if (!res.ok) { toast.error(d?.message || "Failed to resend"); return; }
      toast.success(d?.message || "Invoice resend queued");
      await loadData();
    } catch { toast.error("Failed to resend invoice"); }
    finally { setResendingId(""); }
  };

  const resetSubs = async () => {
    if (!row) return;
    const name = norm(row.tenantName) || "this tenant";
    if (!window.confirm(`Remove all subscription records for ${name}? This resets the tenant to no plan.`)) return;
    setResettingId(tenantId);
    try {
      const res = await apiDelete(`/api/admin/billing/tenants/${encodeURIComponent(tenantId)}/subscriptions`);
      const d = await res.json().catch(() => null);
      if (!res.ok) { toast.error(d?.message || "Failed to reset"); return; }
      toast.success(d?.message || "Subscriptions removed");
      await loadData();
    } catch { toast.error("Failed to reset subscriptions"); }
    finally { setResettingId(""); }
  };

  /* ── Loading / not found ── */
  if (loading) return (
    <div className="admin-workspace">
      <div className="admin-empty-state">Loading tenant billing…</div>
    </div>
  );

  if (!row) return (
    <div className="admin-workspace">
      <div className="admin-empty-state">Tenant not found.</div>
      <button className="admin-btn-secondary" style={{ marginTop: 12 }} onClick={() => navigate("/admin/billing-overview")}>
        Back to Billing Overview
      </button>
    </div>
  );

  const tabCounts = {
    subscriptions: row.subHistory.length,
    payments:      row.payHistory.length,
    invoices:      row.invHistory.length,
    failed:        row.failHistory.length,
    emails:        row.emailHistory.length,
  };

  return (
    <div className="admin-workspace">
      {/* Breadcrumb */}
      <div className="abtd-breadcrumb">
        <button className="abtd-back-btn" onClick={() => navigate("/admin/billing-overview")}>
          Billing Overview
        </button>
        <ChevronDown size={12} className="abtd-sep" />
        <span className="abtd-breadcrumb-current">{row.tenantName}</span>
      </div>

      {/* Header */}
      <div className="admin-shell-header" style={{ marginBottom: 0 }}>
        <div className="admin-shell-copy">
          <span className="admin-shell-eyebrow">Tenant Billing</span>
          <h1 className="admin-shell-title">{row.tenantName}</h1>
          <p className="admin-shell-subtitle">{row.ownerEmail}</p>
        </div>
        <div className="admin-shell-actions">
          {row.latestInv?.id && (
            <button className="admin-btn-secondary"
              disabled={resendingId === row.latestInv.id || resettingId === tenantId}
              onClick={() => resendInvoice(row.latestInv)}>
              <Mail size={14} /> {resendingId === row.latestInv.id ? "Sending…" : "Resend Invoice"}
            </button>
          )}
          <button className="admin-btn-danger"
            disabled={resettingId === tenantId}
            onClick={resetSubs}>
            {resettingId === tenantId ? "Removing…" : "Remove All Subscriptions"}
          </button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="abtd-kpis">
        {[
          ["Plan",           getPlanLabel(row.effSub)],
          ["Status",         <StatusBadge key="s" value={row.lifecycle} />],
          ["Expiry",         <ExpiryBadge key="e" meta={row.expiry} />],
          ["Latest Paid",    row.lastPaidPay ? fmtCurrency(row.lastPaidPay.totalAmount || row.lastPaidPay.amount, row.lastPaidPay.currency) : "—"],
          ["Licensed Seats", fmtSeats(row.licensedSeats)],
          ["Devices",        row.deviceCount],
          ["Issues",         <span key="i" style={{ color: row.issues.length > 0 ? "var(--as-danger)" : "var(--as-success)" }}>{row.issues.length > 0 ? row.issues.length : "None"}</span>],
        ].map(([label, val]) => (
          <div key={label} className="abtd-kpi">
            <span>{label}</span>
            <strong>{val}</strong>
          </div>
        ))}
      </div>

      {/* Tab bar */}
      <div className="admin-tabbar" style={{ borderTop: "1px solid var(--as-border)", borderBottom: "1px solid var(--as-border)", padding: "0 4px" }}>
        {DETAIL_TABS.map(t => (
          <button key={t.id} type="button"
            className={`admin-tab${detailTab === t.id ? " is-active" : ""}`}
            onClick={() => setDetailTab(t.id)}>
            {t.label}
            {tabCounts[t.id] > 0 && <span className="admin-tab-badge">{tabCounts[t.id]}</span>}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="abtd-body">

        {detailTab === "snapshot" && (
          <div>
            <p className="abtd-warn-note">
              ⚠ Remove All Subscriptions deletes plan subscription records, clears feature access, resets seat cap to 5, and preserves payment/invoice audit history.
            </p>
            <div className="abtd-kv-grid">
              {[
                ["Subscription Status", <StatusBadge key="ss" value={row.lifecycle} />],
                ["Billing Email",    row.billingEmail],
                ["Payment Status",  norm(row.effSub?.paymentStatus) || "—"],
                ["Licensed Seats",  fmtSeats(row.effSub?.seatCount || row.licensedSeats)],
                ["Used Seats",      Number(row.effSub?.usedSeatCount || 0)],
                ["Subtotal",        fmtCurrency(row.effSub?.subtotalAmount, row.effSub?.currency)],
                ["GST",             fmtCurrency(row.effSub?.taxAmount, row.effSub?.currency)],
                ["Total",           fmtCurrency(row.effSub?.totalAmount || row.effSub?.amount, row.effSub?.currency)],
                ["Next Billing",    fmtDate(row.effSub?.nextBillingAt)],
                ["Latest Invoice",  row.latestInv?.invoiceNumber || "—"],
                ["Enrollment Key",  <span key="ek" className="admin-monospace">{row.enrollmentKey || "—"}</span>],
              ].map(([label, val]) => (
                <div key={label} className="admin-detail-item">
                  <span>{label}</span>
                  <strong>{val}</strong>
                </div>
              ))}
            </div>
          </div>
        )}

        {detailTab === "subscriptions" && (
          row.subHistory.length === 0
            ? <div className="admin-empty-state">No subscription history.</div>
            : <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead><tr>
                    <th>Plan</th><th>Status</th><th>Seats</th>
                    <th>Base</th><th>GST</th><th>Total</th>
                    <th>Start</th><th>Expiry</th><th>Next Billing</th>
                  </tr></thead>
                  <tbody>{row.subHistory.map(r => (
                    <tr key={r.id}>
                      <td><div className="admin-cell-title">{getPlanLabel(r)}</div></td>
                      <td><StatusBadge value={r.status} /></td>
                      <td>{fmtSeats(r.seatCount)}</td>
                      <td>{fmtCurrency(r.subtotalAmount, r.currency)}</td>
                      <td>{fmtCurrency(r.taxAmount, r.currency)}</td>
                      <td><strong>{fmtCurrency(r.totalAmount || r.amount, r.currency)}</strong></td>
                      <td>{fmtDate(r.startsAt)}</td>
                      <td>{fmtDate(r.expiresAt)}</td>
                      <td>{fmtDate(r.nextBillingAt)}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
        )}

        {detailTab === "payments" && (
          row.payHistory.length === 0
            ? <div className="admin-empty-state">No payment history.</div>
            : <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead><tr>
                    <th>Status</th><th>Seats</th><th>Base</th><th>GST</th><th>Total</th>
                    <th>Method</th><th>Invoice</th><th>Provider Ref</th><th>Paid At</th>
                  </tr></thead>
                  <tbody>{row.payHistory.map(r => (
                    <tr key={r.id}>
                      <td><StatusBadge value={r.status} /></td>
                      <td>{r.agentCount > 0 ? r.agentCount : "—"}</td>
                      <td>{fmtCurrency(r.amount, r.currency)}</td>
                      <td>{fmtCurrency(r.taxAmount, r.currency)}</td>
                      <td><strong>{fmtCurrency(r.totalAmount || r.amount, r.currency)}</strong></td>
                      <td>{norm(r.paymentMethod) || "—"}</td>
                      <td>{r.invoiceNumber || "—"}</td>
                      <td className="admin-monospace">{r.providerPaymentId || r.providerOrderId || "—"}</td>
                      <td>{fmtDate(r.paidAt || r.createdAt, { time: true })}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
        )}

        {detailTab === "invoices" && (
          row.invHistory.length === 0
            ? <div className="admin-empty-state">No invoices found.</div>
            : <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead><tr>
                    <th>Invoice #</th><th>Status</th><th>Base</th><th>GST</th><th>Total</th>
                    <th>Paid At</th><th>Created</th><th>Actions</th>
                  </tr></thead>
                  <tbody>{row.invHistory.map(r => (
                    <tr key={r.id}>
                      <td className="admin-monospace">{r.invoiceNumber || r.id}</td>
                      <td><StatusBadge value={r.status} /></td>
                      <td>{fmtCurrency(r.subtotal, r.currency)}</td>
                      <td>{fmtCurrency(r.taxAmount, r.currency)}</td>
                      <td><strong>{fmtCurrency(r.total, r.currency)}</strong></td>
                      <td>{fmtDate(r.paidAt)}</td>
                      <td>{fmtDate(r.createdAt)}</td>
                      <td>
                        <button className="admin-inline-action"
                          disabled={!r.id || resendingId === r.id}
                          onClick={() => resendInvoice(r)}>
                          {resendingId === r.id ? "Sending…" : "Resend"}
                        </button>
                      </td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
        )}

        {detailTab === "failed" && (
          row.failHistory.length === 0
            ? <div className="admin-empty-state">No failed payments recorded.</div>
            : <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead><tr>
                    <th>Seats</th><th>Base</th><th>GST</th><th>Total</th>
                    <th>Status</th><th>Failure Reason</th><th>Created</th>
                  </tr></thead>
                  <tbody>{row.failHistory.map(r => (
                    <tr key={r.id}>
                      <td>{r.agentCount > 0 ? r.agentCount : "—"}</td>
                      <td>{fmtCurrency(r.amount, r.currency)}</td>
                      <td>{fmtCurrency(r.taxAmount, r.currency)}</td>
                      <td><strong>{fmtCurrency(r.totalAmount || r.amount, r.currency)}</strong></td>
                      <td><StatusBadge value={r.status} /></td>
                      <td style={{ color: "var(--as-danger)" }}>{norm(r.failureReason) || "—"}</td>
                      <td>{fmtDate(r.createdAt, { time: true })}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
        )}

        {detailTab === "emails" && (
          row.emailHistory.length === 0
            ? <div className="admin-empty-state">No billing email activity.</div>
            : <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead><tr><th>Event</th><th>Recipient</th><th>Status</th><th>Sent At</th></tr></thead>
                  <tbody>{row.emailHistory.map(r => (
                    <tr key={r.id}>
                      <td>
                        <div className="admin-cell-title">{norm(r.eventType) || "—"}</div>
                        <div className="admin-cell-subtitle admin-monospace">{r.eventKey || "—"}</div>
                      </td>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.875rem" }}>
                          <Mail size={13} style={{ color: "var(--as-text-3)" }} /> {r.recipient || "—"}
                        </div>
                      </td>
                      <td><StatusBadge value={r.status} /></td>
                      <td>{fmtDate(r.sentAt || r.createdAt, { time: true })}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
        )}
      </div>

      <style>{`
        .abtd-breadcrumb {
          display: flex; align-items: center; gap: 6px; margin-bottom: 20px;
        }
        .abtd-back-btn {
          font-size: 13px; font-weight: 500; color: var(--as-text-2);
          background: none; border: none; padding: 0; cursor: pointer; transition: color .12s;
        }
        .abtd-back-btn:hover { color: var(--as-primary, #0078d4); }
        .abtd-sep { transform: rotate(-90deg); color: var(--as-border); flex-shrink: 0; }
        .abtd-breadcrumb-current { font-size: 13px; font-weight: 600; }
        .abtd-kpis {
          display: flex; flex-wrap: wrap; gap: 0;
          border: 1px solid var(--as-border); border-radius: 4px;
          background: var(--as-surface-2);
          margin: 20px 0 0;
        }
        .abtd-kpi {
          flex: 1 1 120px; padding: 12px 18px;
          border-right: 1px solid var(--as-border);
        }
        .abtd-kpi:last-child { border-right: none; }
        .abtd-kpi span {
          display: block; font-size: .68rem; font-weight: 700;
          text-transform: uppercase; letter-spacing: .05em;
          color: var(--as-text-3); margin-bottom: 3px;
        }
        .abtd-kpi strong {
          font-size: .9rem; font-weight: 700; color: var(--as-text);
        }
        .abtd-body { padding: 20px 0; }
        .abtd-warn-note {
          font-size: 0.8rem; color: var(--as-danger); margin-bottom: 16px;
          padding: 8px 12px; background: var(--as-danger-bg);
          border-radius: 4px; border: 1px solid rgba(164,38,44,.2);
        }
        .abtd-kv-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
          gap: 8px;
        }
      `}</style>
    </div>
  );
}
